import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Text } from '@/components/ui';
import { chartAxisTick, chartColor } from '@/lib/design-system/color-system';
import { CurrencyBadge } from '@/components/finance/CurrencyBadge.web';
import {
  fetchAccounts,
  fetchAllTransactions,
  fetchCategories,
  fetchCurrencies,
  fetchFxRates,
} from '@/lib/finance/api';
import {
  buildBalanceSeries,
  buildBalanceSnapshot,
  sumSelectedUsd,
  usdInCurrency,
  type AccountBalanceRow,
  type BalancePoint,
  type BalanceSnapshot,
} from '@/lib/finance/balances';
import { accountPickerLabel, formatAmount, formatUsd } from '@/lib/finance/format';
import { asOfForMonth, monthLabelShort, monthStart, recentMonths } from '@/lib/finance/month';
import type { Account, Category, Currency, FxRate, Transaction } from '@/lib/finance/types';

const TREND_MONTHS = 12;

/** `null` means every account that is on screen. A set is an explicit choice. */
type AccountSelection = { known: Set<number>; picked: Set<number> } | null;

function formatAsOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d).toLocaleString('default', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTxDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d).toLocaleString('default', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function chosenAccountIds(rows: { accountId: number }[], selection: AccountSelection): Set<number> {
  const ids = rows.map((row) => row.accountId);
  if (selection == null) return new Set(ids);
  const present = new Set(ids);
  const next = new Set<number>();
  for (const id of selection.picked) if (present.has(id)) next.add(id);
  for (const id of ids) if (!selection.known.has(id)) next.add(id);
  return next;
}

export function BalancesReport({ month }: { month: string }) {
  const asOf = asOfForMonth(month);
  const points = useMemo(
    () => recentMonths(month, TREND_MONTHS).map((m) => ({ month: m, asOf: asOfForMonth(m) })),
    [month],
  );

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [rates, setRates] = useState<FxRate[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [totalCurrency, setTotalCurrency] = useState('USD');
  const [selection, setSelection] = useState<AccountSelection>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [accountRows, categoryRows, transactionRows, rateRows, currencyRows] =
          await Promise.all([
            fetchAccounts(),
            fetchCategories(),
            fetchAllTransactions({ to: asOf }),
            fetchFxRates(monthStart(points[0]!.month), monthStart(month)),
            fetchCurrencies(),
          ]);
        if (cancelled) return;
        setAccounts(accountRows);
        setCategories(categoryRows);
        setTransactions(transactionRows);
        setRates(rateRows);
        setCurrencies(currencyRows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to build report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [asOf, month, points]);

  const snapshot = useMemo(
    () => buildBalanceSnapshot(accounts, categories, transactions, asOf, rates),
    [accounts, categories, transactions, asOf, rates],
  );
  const series = useMemo(
    () => buildBalanceSeries(accounts, categories, transactions, points, rates),
    [accounts, categories, transactions, points, rates],
  );
  const labels = useMemo(() => {
    const map = new Map<number, string>();
    for (const account of accounts) map.set(account.id, accountPickerLabel(account));
    return map;
  }, [accounts]);
  const selected = useMemo(
    () => chosenAccountIds(snapshot.rows, selection),
    [snapshot.rows, selection],
  );

  function toggleAccount(accountId: number) {
    const ids = snapshot.rows.map((row) => row.accountId);
    const picked = chosenAccountIds(snapshot.rows, selection);
    if (picked.has(accountId)) picked.delete(accountId);
    else picked.add(accountId);
    setSelection({ known: new Set(ids), picked });
  }

  function toggleAll() {
    const ids = snapshot.rows.map((row) => row.accountId);
    const picked = chosenAccountIds(snapshot.rows, selection);
    const allOn = ids.length > 0 && ids.every((id) => picked.has(id));
    setSelection({ known: new Set(ids), picked: allOn ? new Set() : new Set(ids) });
  }

  if (loading) return <ActivityIndicator />;
  if (error)
    return (
      <Text variant="body-sm" className="text-danger">
        {error}
      </Text>
    );

  return (
    <View>
      <BalancesBody
        snapshot={snapshot}
        series={series}
        labels={labels}
        rates={rates}
        currencyCodes={currencyCodes(currencies)}
        totalCurrency={totalCurrency}
        onTotalCurrency={setTotalCurrency}
        selected={selected}
        onToggle={toggleAccount}
        onToggleAll={toggleAll}
      />
    </View>
  );
}

function currencyCodes(currencies: Currency[]): string[] {
  const rest = [...new Set(currencies.map((currency) => currency.code))]
    .filter((code) => code !== 'USD')
    .sort((a, b) => a.localeCompare(b));
  return ['USD', ...rest];
}

function money(amount: number | null, currency: string): string {
  if (amount == null) return '—';
  if (currency === 'USD') return `$${formatUsd(amount)}`;
  return `${formatAmount(amount)} ${currency}`;
}

function missingCurrencies(rows: AccountBalanceRow[], selected: ReadonlySet<number>): string[] {
  const missing = new Set<string>();
  for (const row of rows) {
    if (!selected.has(row.accountId)) continue;
    if (row.amount !== 0 && row.usd == null) missing.add(row.currencyCode);
  }
  return [...missing].sort();
}

/** Native balance of one account, in the total currency, for one month. */
function cellInCurrency(
  row: AccountBalanceRow | undefined,
  currency: string,
  month: string,
  rates: FxRate[],
): number | null {
  if (!row || row.amount === 0) return 0;
  return usdInCurrency(row.usd, currency, month, rates);
}

function SelectAllBox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={checked ? 'Unselect all accounts' : 'Select all accounts'}
      checked={checked}
      onChange={onChange}
    />
  );
}

function BalancesBody({
  snapshot,
  series,
  labels,
  rates,
  currencyCodes: codes,
  totalCurrency,
  onTotalCurrency,
  selected,
  onToggle,
  onToggleAll,
}: {
  snapshot: BalanceSnapshot;
  series: BalancePoint[];
  labels: Map<number, string>;
  rates: FxRate[];
  currencyCodes: string[];
  totalCurrency: string;
  onTotalCurrency: (code: string) => void;
  selected: ReadonlySet<number>;
  onToggle: (accountId: number) => void;
  onToggleAll: () => void;
}) {
  const selectedUsd = sumSelectedUsd(snapshot.rows, selected);
  const total = usdInCurrency(selectedUsd, totalCurrency, snapshot.asOf, rates);
  const chartData = series.map((point) => ({
    name: monthLabelShort(point.month),
    Balance: usdInCurrency(sumSelectedUsd(point.rows, selected), totalCurrency, point.month, rates),
  }));
  const selectedRows = snapshot.rows.filter((row) => selected.has(row.accountId));
  const allSelected =
    snapshot.rows.length > 0 && snapshot.rows.every((row) => selected.has(row.accountId));
  const someSelected = snapshot.rows.some((row) => selected.has(row.accountId));
  const foreign = snapshot.rows.filter((row) => row.foreignCount > 0);
  const missing = missingCurrencies(snapshot.rows, selected);

  return (
    <View>
      <View className="mb-4 flex-row flex-wrap items-end gap-3">
        <View className="min-w-[180px] flex-1 rounded-lg border border-border px-4 py-3">
          <Text variant="caption" className="text-muted">
            Total as of {formatAsOf(snapshot.asOf)}
          </Text>
          <Text variant="h3">{money(total, totalCurrency)}</Text>
        </View>
      </View>

      {snapshot.rows.length === 0 ? (
        <Text variant="body" className="text-muted">
          No accounts yet. Add them in Master Data.
        </Text>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="w-10 border-b border-border px-3 py-1.5 text-left font-medium text-muted">
                  <SelectAllBox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={onToggleAll}
                  />
                </th>
                <th className="border-b border-border px-3 py-1.5 text-left font-medium text-muted">
                  Account
                </th>
                <th className="border-b border-border px-3 py-1.5 text-left font-medium text-muted">
                  Currency
                </th>
                <th className="border-b border-border px-3 py-1.5 text-right font-medium text-muted">
                  Balance
                </th>
                <th className="border-b border-border px-3 py-1.5 text-right font-medium text-muted">
                  USD
                </th>
                <th className="border-b border-border px-3 py-1.5 text-left font-medium text-muted">
                  Last transaction
                </th>
              </tr>
            </thead>
            <tbody>
              {snapshot.rows.map((row) => {
                const name = labels.get(row.accountId) ?? row.name;
                return (
                  <tr key={row.accountId}>
                    <td className="border-b border-border px-3 py-1.5">
                      <input
                        type="checkbox"
                        aria-label={`Include ${name}`}
                        checked={selected.has(row.accountId)}
                        onChange={() => onToggle(row.accountId)}
                      />
                    </td>
                    <td className="border-b border-border px-3 py-1.5">{name}</td>
                    <td className="border-b border-border px-3 py-1.5">
                      <CurrencyBadge code={row.currencyCode} />
                    </td>
                    <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                      {formatAmount(row.amount)}
                    </td>
                    <td className="border-b border-border px-3 py-1.5 text-right tabular-nums">
                      {row.usd == null ? '—' : formatUsd(row.usd)}
                    </td>
                    <td className="border-b border-border px-3 py-1.5 tabular-nums text-muted">
                      {row.lastTransactionDate ? formatTxDate(row.lastTransactionDate) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-surface-raised">
                <td className="border-t-2 border-border-strong px-3 py-2" />
                <td className="border-t-2 border-border-strong px-3 py-2 font-semibold">Total</td>
                <td className="border-t-2 border-border-strong px-3 py-2">
                  <select
                    aria-label="Total currency"
                    className="cs-input h-8 w-28 py-0"
                    value={totalCurrency}
                    onChange={(event) => onTotalCurrency(event.target.value)}
                  >
                    {codes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums">
                  {money(total, totalCurrency)}
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2 text-right font-semibold tabular-nums">
                  {selectedUsd == null ? '—' : formatUsd(selectedUsd)}
                </td>
                <td className="border-t-2 border-border-strong px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <View className="mt-6">
        <Text variant="h3" className="mb-2">
          Last {TREND_MONTHS} months
        </Text>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid stroke={chartColor.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={chartAxisTick} />
              <YAxis tick={chartAxisTick} />
              <Tooltip
                formatter={(value: unknown) =>
                  value == null || Number.isNaN(Number(value))
                    ? '—'
                    : money(Number(value), totalCurrency)
                }
              />
              <Line
                type="monotone"
                dataKey="Balance"
                stroke={chartColor.series}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </View>

      {selectedRows.length === 0 ? (
        <Text variant="body" className="mt-6 text-muted">
          Select an account to see balances by month.
        </Text>
      ) : (
        <View className="mt-6">
          <Text variant="h3" className="mb-2">
            Balances by month
          </Text>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border-b border-border px-3 py-1.5 text-left font-medium text-muted">
                    Account
                  </th>
                  {series.map((point) => (
                    <th
                      key={point.month}
                      className="border-b border-border px-2 py-1.5 text-right font-medium text-muted"
                    >
                      {monthLabelShort(point.month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedRows.map((account) => (
                  <tr key={account.accountId}>
                    <td className="border-b border-border px-3 py-1.5">
                      <div>{labels.get(account.accountId) ?? account.name}</div>
                      <CurrencyBadge code={account.currencyCode} />
                    </td>
                    {series.map((point) => {
                      const row = point.rows.find((item) => item.accountId === account.accountId);
                      const value = cellInCurrency(row, totalCurrency, point.month, rates);
                      return (
                        <td
                          key={point.month}
                          className="border-b border-border px-2 py-1.5 text-right tabular-nums"
                        >
                          {money(value, totalCurrency)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-raised">
                  <td className="border-t-2 border-border-strong px-3 py-2 font-semibold">Total</td>
                  {series.map((point) => (
                    <td
                      key={point.month}
                      className="border-t-2 border-border-strong px-2 py-2 text-right font-semibold tabular-nums"
                    >
                      {money(
                        usdInCurrency(
                          sumSelectedUsd(point.rows, selected),
                          totalCurrency,
                          point.month,
                          rates,
                        ),
                        totalCurrency,
                      )}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </View>
      )}

      <Text variant="caption" className="mt-3 text-muted">
        Balance is the sum of transactions through this date. Income adds, expenses subtract, and a
        transfer leaves one account and enters the other. The total, the line, and the month table
        include only the checked accounts, in the currency chosen on the total row. To change a
        balance, record a transaction.
        {missing.length > 0
          ? ` Missing an FX rate for ${missing.join(', ')}, so those accounts are left out of the total.`
          : ''}
        {selectedUsd != null && total == null
          ? ` No FX rate for ${totalCurrency} this month, so the total stays blank.`
          : ''}
        {foreign.length > 0
          ? ` Left out of the native balance because the transaction currency differs: ${foreign
              .map((row) => labels.get(row.accountId) ?? row.name)
              .join(', ')}.`
          : ''}
        {snapshot.unassignedCount > 0
          ? ` ${snapshot.unassignedCount} transaction${snapshot.unassignedCount === 1 ? '' : 's'} with no account ${snapshot.unassignedCount === 1 ? 'is' : 'are'} not included.`
          : ''}
      </Text>
    </View>
  );
}
